import { useState, useEffect, useLayoutEffect, RefObject, useCallback } from 'react';
import { useConfig } from '../../../utils/config';
import { KonvaArtStageRefs } from '../components/KonvaCanvas';

export interface LayoutState {
  panelSize: number;
  aiPanelSize: number;
  panelOrientation: 'horizontal' | 'vertical';
  reflowStage: boolean;
  toolbarDrawOrientation: 'horizontal' | 'vertical';
  updateCanvasSize: () => void;
  initializeStageSize: (stageRef: RefObject<SVGSVGElement>) => void;
}

/**
 * Hook for managing the layout of the art editor
 * Handles responsive sizing, orientation, and panel dimensions
 * 
 * @param stageRef Reference to the Konva stage for canvas resizing
 */
export const useLayoutManager = (
  stageRef: RefObject<KonvaArtStageRefs>
): LayoutState => {
  const config = useConfig();
  const [panelOrientation, setPanelOrientation] = useState<'horizontal' | 'vertical'>('horizontal');
  const [panelSize, setPanelSize] = useState(config.IMAGE_SIZE);
  const [aiPanelSize, setAiPanelSize] = useState(config.IMAGE_SIZE);
  const [reflowStage, setReflowStage] = useState(false);
  // Currently hardcoded in ArtEdit, but could be made responsive in the future
  const toolbarDrawOrientation = 'horizontal';
  const MIN_AI_PANEL_SIZE = 160;

  // Handle window resize and calculate panel dimensions
  useLayoutEffect(() => {
    const handleResize = () => {
      const DESKTOP = 768;
      const MOBILE = 512;

      // Calculate available space for panels
      const PADDING = window.innerWidth < DESKTOP ? 10 : window.innerWidth < MOBILE ? 5 : 40; // Reduce padding for smaller screens
      const TOOLBAR_HEIGHT = 60; // Approximate height of the toolbar
      const DESCRIPTION_HEIGHT = 120; // Approximate height of the description input
      const GAP_BETWEEN_PANELS = 10; // Gap between panels
      
      // Calculate available width and height
      const availableWidth = window.innerWidth - (PADDING * 2);
      const availableHeight = window.innerHeight - TOOLBAR_HEIGHT - DESCRIPTION_HEIGHT - (PADDING * 2);
      
      // Determine orientation based on window dimensions
      const isHorizontal = window.innerWidth > window.innerHeight;
      setPanelOrientation(isHorizontal ? 'horizontal' : 'vertical');
      
      // Calculate panel sizes based on orientation and screen width
      let userSize, aiSize;
      
      if (window.innerWidth < DESKTOP) {
        // On smaller screens, prioritize the user drawing panel
        if (isHorizontal) {
          // In horizontal layout, panels are side by side
          // Calculate maximum possible size for user panel
          userSize = Math.min(
            Math.floor(availableWidth * 0.6), // Give user panel 60% of width
            availableHeight // Height constraint
          );
          
          // Calculate remaining space for AI panel
          const remainingWidth = availableWidth - userSize - GAP_BETWEEN_PANELS;
          aiSize = Math.min(
            remainingWidth,
            availableHeight
          );
          
          // Ensure AI panel is at least MIN_AI_PANEL_SIZE
          if (aiSize < MIN_AI_PANEL_SIZE) {
            aiSize = MIN_AI_PANEL_SIZE;
            // Recalculate user panel size if necessary
            userSize = Math.min(
              availableWidth - MIN_AI_PANEL_SIZE - GAP_BETWEEN_PANELS,
              availableHeight
            );
          }
        } else {
          // In vertical layout, panels are stacked
          // Calculate maximum possible size for user panel
          userSize = Math.min(
            availableWidth, // Width constraint
            Math.floor(availableHeight * 0.6) // Give user panel 60% of height
          );
          
          // Calculate remaining space for AI panel
          const remainingHeight = availableHeight - userSize - GAP_BETWEEN_PANELS;
          aiSize = Math.min(
            availableWidth,
            remainingHeight
          );
          
          // Ensure AI panel is at least MIN_AI_PANEL_SIZE
          if (aiSize < MIN_AI_PANEL_SIZE) {
            aiSize = MIN_AI_PANEL_SIZE;
            // Recalculate user panel size if necessary
            userSize = Math.min(
              availableWidth,
              availableHeight - MIN_AI_PANEL_SIZE - GAP_BETWEEN_PANELS
            );
          }
        }
      } else {
        // On larger screens, keep panels equal size
        if (isHorizontal) {
          // In horizontal layout, panels are side by side
          userSize = aiSize = Math.min(
            (availableWidth - GAP_BETWEEN_PANELS) / 2, // Width constraint
            availableHeight // Height constraint
          );
        } else {
          // In vertical layout, panels are stacked
          userSize = aiSize = Math.min(
            availableWidth, // Width constraint
            (availableHeight - GAP_BETWEEN_PANELS) / 2 // Height constraint
          );
        }
      }
      
      // Ensure panel sizes are at least a minimum value and are integers
      const MIN_SIZE = 200;
      userSize = Math.max(MIN_SIZE, Math.floor(userSize));
      aiSize = Math.max(MIN_AI_PANEL_SIZE, Math.floor(aiSize));
      
      // Update panel sizes
      setPanelSize(userSize);
      setAiPanelSize(aiSize);
      
      // For backward compatibility with the existing reflow logic
      const horizontalToolbarWidth = (116+10)*2;
      const reflow = window.innerWidth - (userSize + aiSize) - horizontalToolbarWidth <= 0;
      setReflowStage(reflow);
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Call once on mount

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Initialize stage size
  const initializeStageSize = useCallback((stageRef: RefObject<SVGSVGElement>) => {
    const stage = stageRef.current;
    if (!stage) return;

    stage.setAttribute("width", aiPanelSize.toString());
    stage.setAttribute("height", aiPanelSize.toString());
  }, [aiPanelSize]);

  // Update canvas when panel size changes
  const updateCanvasSize = useCallback(() => {
    if (stageRef.current?.drawStageRef.current) {
      const stage = stageRef.current.drawStageRef.current;
      stage.width(panelSize);
      stage.height(panelSize);
      stage.batchDraw();
    }
  }, [panelSize, stageRef]);

  // Update canvas size when panel size changes
  useEffect(() => {
    updateCanvasSize();
  }, [panelSize, updateCanvasSize]);

  return {
    panelSize,
    aiPanelSize,
    panelOrientation,
    reflowStage,
    toolbarDrawOrientation,
    updateCanvasSize,
    initializeStageSize
  };
};